import { useState, type FormEvent, type ReactElement } from 'react';
import { Grid } from '@vwpfs/mary-air-lib/components/air/03-base/Grid';
import { Button } from '@vwpfs/mary-air-lib/components/air/05-atoms/Button';
import { Heading, Headings } from '@vwpfs/mary-air-lib/components/air/05-atoms/Heading';
import { FormField, FieldTypes } from '@vwpfs/mary-air-lib/components/air/07-organisms/Form/Field';
import { ThemePalette } from '@vwpfs/mary-air-lib/theme/_Types';
import { useVehicleFilterOptions } from '../hooks/useVehicleFilterOptions';
import { PAGE_LIMIT, useVehicleSearch } from '../hooks/useVehicleSearch';
import { VehicleResultsTable } from '../components/VehicleResultsTable';
import type { VehicleSearchFilters } from '../types/vehicle';

const EMPTY_FILTERS: VehicleSearchFilters = {};

export function SearchPage(): ReactElement {
  const [id, setId] = useState('');
  const [komNr, setKomNr] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [dealer, setDealer] = useState('');
  const [activeFilters, setActiveFilters] = useState<VehicleSearchFilters>(EMPTY_FILTERS);
  const [pageNumber, setPageNumber] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);

  const { data, isFetching, isError, error } = useVehicleSearch(activeFilters, pageNumber);
  const { data: brandOptions, isPending: optionsPending, isError: optionsFailed } = useVehicleFilterOptions();

  // Models belong to a brand, so the second dropdown is empty until the first is answered. Nesting
  // in the response is what makes narrowing it free of another request.
  const modelOptions = brandOptions?.find((option) => option.name === brand)?.models ?? [];

  const selectOptions = (values: { name: string }[]): { label: string; value: string }[] =>
    values.map(({ name }) => ({ label: name, value: name }));

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const cleanedId = id.trim();
    const cleanedKomNr = komNr.trim();
    const cleanedLicensePlate = licensePlate.trim();
    const cleanedBrand = brand.trim();
    const cleanedModel = model.trim();
    const cleanedDealer = dealer.trim();

    if (!cleanedId && !cleanedKomNr && !cleanedLicensePlate && !cleanedBrand && !cleanedModel && !cleanedDealer) {
      setValidationError(
        'Please enter at least one search criterion (id, KomNr, license plate, brand, model, or dealer).'
      );
      return;
    }

    // Caught here as well as server-side: the backend rejects a non-numeric id, and a form error
    // reads better than a failed request.
    if (cleanedId && !/^\d+$/.test(cleanedId)) {
      setValidationError('Id must be a number.');
      return;
    }

    setValidationError(null);
    setActiveFilters({
      id: cleanedId || undefined,
      komNr: cleanedKomNr || undefined,
      licensePlate: cleanedLicensePlate || undefined,
      brand: cleanedBrand || undefined,
      model: cleanedModel || undefined,
      dealer: cleanedDealer || undefined
    });
    setPageNumber(0);
  };

  const onReset = (): void => {
    setId('');
    setKomNr('');
    setLicensePlate('');
    setBrand('');
    setModel('');
    setDealer('');
    setValidationError(null);
    setActiveFilters(EMPTY_FILTERS);
    setPageNumber(0);
  };

  const items = data?.items ?? [];
  const hasActiveSearch = Object.values(activeFilters).some(Boolean);
  const totalCount = data?.count ?? items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_LIMIT));
  const rangeStart = totalCount === 0 ? 0 : pageNumber * PAGE_LIMIT + 1;
  const rangeEnd = Math.min(totalCount, pageNumber * PAGE_LIMIT + items.length);

  return (
    <>
      <header>
        <Heading level={Headings.H1}>Vehicle search</Heading>
      </header>

      <form onSubmit={onSubmit} className="vap-search-form">
        <fieldset className="vap-search-fieldset">
          <legend>Filters</legend>

          {/*
           * Mary Air's own 12-column grid: `.scl-b-row` supplies the 20px gutters and the clearfix,
           * `Grid size` the column spans. Hand-rolled columns looked foreign next to the rest of the
           * app because the library's grid is float-based on a 20px rhythm, not flex on a 16px one.
           *
           * Spans are chosen so both lines fill 12 and no field is wider than its content warrants:
           *   Id 2 · KomNr 2 · dealer 3 · plate 5
           *   brand 6 · model 6
           *
           * Dealer sits on the first line to close it off - a hole at the end of a row is what read
           * as lopsided - and *before* the plate, not after. The plate graphic is 296px inside a
           * 369px column, so with the plate in the middle that leftover width opened a gap and made
           * Dealer look detached. Last in the row, the slack falls at the edge where it reads as
           * margin. The two dropdowns get half the row each, which they need: a model like
           * "Q4 e-tron" under a brand like "Mercedes-Benz" is a long string.
           *
           * The plate takes five columns rather than four because `lg` begins at 1366px, where four
           * columns measure 287px and the plate is a fixed 296px SVG - it would overflow. Below `lg`
           * it takes a whole line, for the same reason.
           */}
          <div className="scl-b-row">
            <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
              <FormField
                type={FieldTypes.Text}
                label="Id"
                value={id}
                onChange={(v: string | undefined) => setId(v ?? '')}
                placeholder="e.g. 123456"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
              <FormField
                type={FieldTypes.Text}
                label="KomNr"
                value={komNr}
                onChange={(v: string | undefined) => setKomNr(v ?? '')}
                placeholder="e.g. 4711"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <FormField
                type={FieldTypes.Text}
                label="Dealer"
                value={dealer}
                onChange={(v: string | undefined) => setDealer(v ?? '')}
                placeholder="e.g. Pon Amsterdam"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, lg: 5 }}>
              <FormField
                type={FieldTypes.LicensePlate}
                label="License plate"
                value={licensePlate}
                onChange={(v: string | undefined) => setLicensePlate(v ?? '')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
              <FormField
                type={FieldTypes.Select}
                label="Brand"
                value={brand}
                options={selectOptions(brandOptions ?? [])}
                disabled={optionsPending || optionsFailed}
                placeholder={optionsFailed ? 'Options unavailable' : 'Any brand'}
                onChange={(v: string | undefined) => {
                  setBrand(v ?? '');
                  // The chosen model almost certainly does not belong to the new brand.
                  setModel('');
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
              <FormField
                type={FieldTypes.Select}
                label="Model"
                value={model}
                options={selectOptions(modelOptions)}
                disabled={!brand || optionsFailed}
                placeholder={brand ? 'Any model' : 'Pick a brand first'}
                onChange={(v: string | undefined) => setModel(v ?? '')}
              />
            </Grid>
          </div>

          <div className="vap-search-buttons">
            <Button buttonType="button" theme={{ palette: ThemePalette.CONTRAST_TERTIARY }} link={{ onClick: onReset }}>
              Reset
            </Button>
            <Button buttonType="submit" theme={{ palette: ThemePalette.BRAND_PRIMARY }}>
              Search
            </Button>
          </div>
        </fieldset>
      </form>

      {validationError && <p className="vap-search-error">{validationError}</p>}

      {/* Dropdown-only means a failed options load removes the filter entirely, so say so. */}
      {optionsFailed && (
        <p className="vap-search-error">Brand and model options could not be loaded. The other filters still work.</p>
      )}

      <section className="vap-results-section">
        {!hasActiveSearch && (
          <p>
            Enter one or more search criteria (id, KomNr, license plate, brand, model, or dealer) and press Search to
            see results.
          </p>
        )}
        {hasActiveSearch && isError && <p className="vap-search-error">Search failed: {error?.message}</p>}
        {hasActiveSearch && !isError && items.length === 0 && !isFetching && <p>No vehicles match the filters.</p>}
        {hasActiveSearch && !isError && items.length > 0 && (
          <>
            <div className="vap-results-header">
              <span>
                Showing <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong> of <strong>{totalCount}</strong>
              </span>
              {isFetching && <span className="vap-results-loading">Loading…</span>}
            </div>
            <VehicleResultsTable items={items} />
            <div className="vap-results-pagination">
              <Button
                buttonType="button"
                theme={{ palette: ThemePalette.CONTRAST_TERTIARY }}
                link={{ onClick: () => setPageNumber((p) => Math.max(0, p - 1)) }}
                disabled={pageNumber === 0 || isFetching}
              >
                ‹ Previous
              </Button>
              <span>
                Page <strong>{pageNumber + 1}</strong> of <strong>{totalPages}</strong>
              </span>
              <Button
                buttonType="button"
                theme={{ palette: ThemePalette.CONTRAST_TERTIARY }}
                link={{ onClick: () => setPageNumber((p) => Math.min(totalPages - 1, p + 1)) }}
                disabled={pageNumber + 1 >= totalPages || isFetching}
              >
                Next ›
              </Button>
            </div>
          </>
        )}
        {hasActiveSearch && isFetching && items.length === 0 && <p>Loading…</p>}
      </section>
    </>
  );
}
